import React, { useCallback } from 'react';
import type { MultiPolygon, Polygon } from 'geojson';
import { FindFilterValue } from '../../FindToolbox/Filters/types';
import {
  DEFAULT_FIND_VISUALIZATION,
  FindVisualization,
} from '../../MapView/visualization';

export type SelectedMapEntity =
  | { type: 'find'; id: string }
  | { type: 'isad'; id: number; name: string }
  | {
      type: 'lpis';
      id: number;
      code: string;
      userName?: string | null;
      userId?: number | null;
      areaHa?: number | null;
      geometry?: Polygon | MultiPolygon | null;
      candidateIds: number[];
    };

interface FindFilter extends FindFilterValue {
  id: string;
}

interface SiteFilter {
  eras?: string[];
  tags?: string[];
}

type SettingsStoreState = {
  mapDrawerOpen: boolean;
  mainDrawerIndex: number;
  findFilters: FindFilter[];
  siteFilters: SiteFilter[];
  activeFindFilter: FindFilter | null;
  selectedEntity: SelectedMapEntity | null;
  selectedObject: string | null;
  findLayersEnabled: boolean;
  heatmapLayerEnabled: boolean;
  isadLayerEnabled: boolean;
  lpisLayerEnabled: boolean;
  objectLayerEnabled: boolean;
  objectsVisibleOnMap: boolean;
  parentsVisibleOnMap: boolean;
  findVisualization: FindVisualization;
  hideVisualizationDefaults: boolean;
  findSettingsExpanded: boolean;
};

enum SettingsActionType {
  SET_MAIN_DRAWER_INDEX = 'SET_MAIN_DRAWER_INDEX',
  CREATE_FIND_FILTER = 'CREATE_FIND_FILTER',
  REPLACE_FIND_FILTER = 'REPLACE_FIND_FILTER',
  UPDATE_FIND_FILTER = 'UPDATE_FIND_FILTER',
  DISABLE_FIND_FILTER = 'DISABLE_FIND_FILTER',
  TOGGLE_FIND_LAYERS = 'TOGGLE_FIND_LAYERS',
  TOGGLE_HEATMAP_LAYER = 'TOGGLE_HEATMAP_LAYER',
  TOGGLE_ISAD_LAYER = 'TOGGLE_ISAD_LAYER',
  TOGGLE_LPIS_LAYER = 'TOGGLE_LPIS_LAYER',
  TOGGLE_MAP_DRAWER = 'TOGGLE_MAP_DRAWER',
  TOGGLE_OBJECT_LAYER = 'TOGGLE_OBJECT_LAYER',
  TOGGLE_OBJECTS_MAP_VISIBILITY = 'TOGGLE_OBJECTS_MAP_VISIBILITY',
  TOGGLE_PARENTS_MAP_VISIBILITY = 'TOGGLE_PARENTS_MAP_VISIBILITY',
  SET_FIND_VISUALIZATION = 'SET_FIND_VISUALIZATION',
  TOGGLE_VISUALIZATION_DEFAULTS = 'TOGGLE_VISUALIZATION_DEFAULTS',
  SET_FIND_SETTINGS_EXPANDED = 'SET_FIND_SETTINGS_EXPANDED',
  APPLY_FIND_VISUALIZATION = 'APPLY_FIND_VISUALIZATION',
  SET_SELECTED_ENTITY = 'SET_SELECTED_ENTITY',
  SET_SELECTED_OBJECT = 'SET_SELECTED_OBJECT',
}

type SettingsStoreAction = {
  type: SettingsActionType;
  payload?: any;
};

const settingsReducer = (
  state: SettingsStoreState,
  action: SettingsStoreAction
) => {
  switch (action.type) {
    case 'CREATE_FIND_FILTER':
      const newFilter = {
        id: new Date().toISOString(),
        stored: false,
        ...action.payload,
      };
      return {
        ...state,
        findFilters: [...state.findFilters, newFilter],
        activeFindFilter: newFilter,
      };
    case 'REPLACE_FIND_FILTER': {
      const replacement = {
        ...action.payload,
        id: new Date().toISOString(),
        stored: false,
      };

      return {
        ...state,
        findFilters: [...state.findFilters, replacement],
        activeFindFilter: replacement,
      };
    }
    case 'DISABLE_FIND_FILTER':
      return {
        ...state,
        activeFindFilter: null,
      };
    case 'UPDATE_FIND_FILTER':
      // TODO: use this when we have saving implemented return { ...state, findFilters: [...state.findFilters, action.payload] };
      const updatedFilter = action.payload;
      return {
        ...state,
        findFilters: updatedFilter.stored
          ? state.findFilters
          : [
              ...state.findFilters.filter((f) => f.id !== updatedFilter.id),
              updatedFilter,
            ],
        activeFindFilter: updatedFilter,
      };
    case 'TOGGLE_FIND_LAYERS':
      return {
        ...state,
        findLayersEnabled: !state.findLayersEnabled,
      };
    case 'TOGGLE_HEATMAP_LAYER':
      return {
        ...state,
        heatmapLayerEnabled: !state.heatmapLayerEnabled,
      };
    case 'TOGGLE_ISAD_LAYER':
      return {
        ...state,
        isadLayerEnabled: !state.isadLayerEnabled,
      };
    case 'TOGGLE_LPIS_LAYER':
      return {
        ...state,
        lpisLayerEnabled: !state.lpisLayerEnabled,
      };
    case 'TOGGLE_OBJECT_LAYER':
      return {
        ...state,
        objectLayerEnabled: !state.objectLayerEnabled,
      };
    case 'TOGGLE_OBJECTS_MAP_VISIBILITY':
      return {
        ...state,
        objectsVisibleOnMap: !state.objectsVisibleOnMap,
      };
    case 'TOGGLE_PARENTS_MAP_VISIBILITY':
      return {
        ...state,
        parentsVisibleOnMap: !state.parentsVisibleOnMap,
      };
    case 'TOGGLE_MAP_DRAWER':
      return {
        ...state,
        mapDrawerOpen: !state.mapDrawerOpen,
      };
    case 'SET_MAIN_DRAWER_INDEX':
      return {
        ...state,
        mainDrawerIndex: action.payload,
      };
    case 'SET_SELECTED_ENTITY':
      return {
        ...state,
        selectedEntity: action.payload,
        mainDrawerIndex: 0,
        mapDrawerOpen: true,
      };
    case 'SET_SELECTED_OBJECT':
      return {
        ...state,
        selectedObject: action.payload,
      };
    case 'SET_FIND_VISUALIZATION':
      return { ...state, findVisualization: action.payload };
    case 'TOGGLE_VISUALIZATION_DEFAULTS':
      return {
        ...state,
        hideVisualizationDefaults: !state.hideVisualizationDefaults,
      };
    case 'SET_FIND_SETTINGS_EXPANDED':
      return { ...state, findSettingsExpanded: action.payload };
    case 'APPLY_FIND_VISUALIZATION':
      return {
        ...state,
        findVisualization: action.payload,
        hideVisualizationDefaults: false,
        findSettingsExpanded: true,
        mainDrawerIndex: 0,
        mapDrawerOpen: true,
      };
    default:
      return state;
  }
};

function createCtx(defaultValue: SettingsStoreState) {
  const ctx = React.createContext({
    store: defaultValue,
    setMainDrawerIndex: null,
    setSelectedEntity: null,
    setSelectedObject: null,
    addFindsFilter: null,
    replaceFindsFilter: null,
    disableFindsFilter: null,
    updateFindsFilter: null,
    toggleFindLayer: null,
    toggleHeatmapLayer: null,
    toggleIsadLayer: null,
    toggleLpisLayer: null,
    toggleObjectLayer: null,
    toggleObjectsMapVisibility: null,
    toggleParentsMapVisibility: null,
    toggleMapDrawer: null,
    setFindVisualization: null,
    toggleVisualizationDefaults: null,
    setFindSettingsExpanded: null,
    applyFindVisualization: null,
  });

  function SettingsProvider(props: React.PropsWithChildren<{}>) {
    const [store, dispatch] = React.useReducer(settingsReducer, defaultValue);

    const setMainDrawerIndex = useCallback(
      (index) => {
        dispatch({
          type: SettingsActionType.SET_MAIN_DRAWER_INDEX,
          payload: index,
        });
      },
      [dispatch]
    );

    const setSelectedEntity = useCallback(
      (entity: SelectedMapEntity | null) => {
        dispatch({
          type: SettingsActionType.SET_SELECTED_ENTITY,
          payload: entity,
        });
      },
      [dispatch]
    );

    const setSelectedObject = useCallback(
      (id: string | null) => {
        dispatch({
          type: SettingsActionType.SET_SELECTED_OBJECT,
          payload: id,
        });
      },
      [dispatch]
    );

    const addFindsFilter = useCallback(
      (options) => {
        dispatch({
          type: SettingsActionType.CREATE_FIND_FILTER,
          payload: options,
        });
      },
      [dispatch]
    );

    const replaceFindsFilter = useCallback(
      (filter: FindFilterValue) => {
        dispatch({
          type: SettingsActionType.REPLACE_FIND_FILTER,
          payload: filter,
        });
      },
      [dispatch]
    );

    const disableFindsFilter = useCallback(() => {
      dispatch({ type: SettingsActionType.DISABLE_FIND_FILTER });
    }, [dispatch]);

    const updateFindsFilter = useCallback(
      (newFilter) => {
        dispatch({
          type: SettingsActionType.UPDATE_FIND_FILTER,
          payload: newFilter,
        });
      },
      [dispatch]
    );

    const toggleFindLayer = useCallback(
      () => dispatch({ type: SettingsActionType.TOGGLE_FIND_LAYERS }),
      [dispatch]
    );

    const toggleHeatmapLayer = useCallback(
      () => dispatch({ type: SettingsActionType.TOGGLE_HEATMAP_LAYER }),
      [dispatch]
    );

    const toggleIsadLayer = useCallback(
      () => dispatch({ type: SettingsActionType.TOGGLE_ISAD_LAYER }),
      [dispatch]
    );

    const toggleLpisLayer = useCallback(
      () => dispatch({ type: SettingsActionType.TOGGLE_LPIS_LAYER }),
      [dispatch]
    );

    const toggleObjectLayer = useCallback(
      () => dispatch({ type: SettingsActionType.TOGGLE_OBJECT_LAYER }),
      [dispatch]
    );

    const toggleObjectsMapVisibility = useCallback(
      () =>
        dispatch({ type: SettingsActionType.TOGGLE_OBJECTS_MAP_VISIBILITY }),
      [dispatch]
    );

    const toggleParentsMapVisibility = useCallback(
      () =>
        dispatch({ type: SettingsActionType.TOGGLE_PARENTS_MAP_VISIBILITY }),
      [dispatch]
    );

    const toggleMapDrawer = useCallback(
      () => dispatch({ type: SettingsActionType.TOGGLE_MAP_DRAWER }),
      [dispatch]
    );

    const setFindVisualization = useCallback(
      (payload: FindVisualization) => {
        dispatch({
          type: SettingsActionType.SET_FIND_VISUALIZATION,
          payload,
        });
      },
      [dispatch]
    );

    const toggleVisualizationDefaults = useCallback(
      () =>
        dispatch({
          type: SettingsActionType.TOGGLE_VISUALIZATION_DEFAULTS,
        }),
      [dispatch]
    );

    const setFindSettingsExpanded = useCallback(
      (expanded: boolean) =>
        dispatch({
          type: SettingsActionType.SET_FIND_SETTINGS_EXPANDED,
          payload: expanded,
        }),
      [dispatch]
    );

    const applyFindVisualization = useCallback(
      (visualization: FindVisualization) =>
        dispatch({
          type: SettingsActionType.APPLY_FIND_VISUALIZATION,
          payload: visualization,
        }),
      [dispatch]
    );

    const value = React.useMemo(
      () => ({
        store,
        addFindsFilter,
        replaceFindsFilter,
        disableFindsFilter,
        setMainDrawerIndex,
        setSelectedEntity,
        setSelectedObject,
        updateFindsFilter,
        toggleFindLayer,
        toggleHeatmapLayer,
        toggleIsadLayer,
        toggleLpisLayer,
        toggleObjectLayer,
        toggleObjectsMapVisibility,
        toggleParentsMapVisibility,
        toggleMapDrawer,
        setFindVisualization,
        toggleVisualizationDefaults,
        setFindSettingsExpanded,
        applyFindVisualization,
      }),
      [
        store,
        addFindsFilter,
        replaceFindsFilter,
        disableFindsFilter,
        setMainDrawerIndex,
        setSelectedEntity,
        setSelectedObject,
        updateFindsFilter,
        toggleFindLayer,
        toggleIsadLayer,
        toggleLpisLayer,
        toggleHeatmapLayer,
        toggleObjectLayer,
        toggleObjectsMapVisibility,
        toggleParentsMapVisibility,
        toggleMapDrawer,
        setFindVisualization,
        toggleVisualizationDefaults,
        setFindSettingsExpanded,
        applyFindVisualization,
      ]
    );

    return <ctx.Provider value={value} {...props} />;
  }

  return [ctx, SettingsProvider] as [typeof ctx, typeof SettingsProvider];
}

const [SettingsContext, SettingsProvider] = createCtx({
  mainDrawerIndex: 0,
  mapDrawerOpen: true,
  activeFindFilter: null,
  findFilters: [],
  siteFilters: [],
  selectedEntity: null,
  selectedObject: null,
  findLayersEnabled: true,
  heatmapLayerEnabled: false,
  isadLayerEnabled: false,
  lpisLayerEnabled: false,
  objectLayerEnabled: false,
  objectsVisibleOnMap: true,
  parentsVisibleOnMap: false,
  findVisualization: DEFAULT_FIND_VISUALIZATION,
  hideVisualizationDefaults: false,
  findSettingsExpanded: false,
});

export { SettingsContext, SettingsProvider };
